import { useCallback, useEffect, useRef } from 'react';
import { Button, ButtonProps, ButtonVariantType } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useGivebutterFormContext } from 'components/form/useGivebutterFormContext';

const SUCCESS_SUBMIT_RESET_TIMEOUT = 3000;

interface UseFormHelpersErrorProps extends Omit<ButtonProps, 'variant'> {
    variant?: ButtonVariantType;
    noAnimation?: boolean;
    isSubmitting?: boolean;
}

const UseFormSubmitButton = (props: UseFormHelpersErrorProps) => {
    const {
        name,
        variant = 'solid',
        disabled,
        noAnimation = false,
        children = 'Submit',
        isSubmitting: isSubmittingConsumer = false,
        ...rest
    } = props;
    const {
        formState: { isSubmitting, isSubmitSuccessful },
        reset,
    } = useGivebutterFormContext();
    const successfulSubmitTimeout = useRef<NodeJS.Timeout>();

    const clearSuccessfulSubmitTimeout = () =>
        clearTimeout(successfulSubmitTimeout.current as NodeJS.Timeout);

    const resetState = useCallback(() => {
        reset(undefined, {
            keepIsSubmitted: true,
            keepDirty: true,
            keepErrors: true,
            keepValues: true,
            keepTouched: true,
            keepSubmitCount: true,
            keepDefaultValues: true,
        });
    }, [reset]);

    useEffect(() => {
        if (isSubmitting || isSubmitSuccessful) {
            clearSuccessfulSubmitTimeout();
        }

        if (isSubmitSuccessful) {
            successfulSubmitTimeout.current = setTimeout(() => {
                resetState();
            }, SUCCESS_SUBMIT_RESET_TIMEOUT);
        }

        return () => {
            clearSuccessfulSubmitTimeout();
        };
    }, [isSubmitSuccessful, isSubmitting, resetState]);

    return (
        <Button
            variant={variant}
            disabled={disabled || isSubmitting || isSubmittingConsumer}
            type={'submit'}
            {...rest}
        >
            {!noAnimation && (isSubmitting || isSubmittingConsumer) && (
                <Loader size="sm" variant="static" className="text-white" />
            )}
            {!noAnimation && !(isSubmitting || isSubmittingConsumer) && isSubmitSuccessful && (
                <FontAwesomeIcon icon={faCheck} />
            )}
            {children}
        </Button>
    );
};

export { UseFormSubmitButton };
